import React, { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import { OrgChartRow, isManagerEmail } from '../../../support/orgScope';
import { useSfTeamOpportunities } from '../../../hooks/useSfTeamOpportunities';
import { useSfTeamActivitySummary } from '../../../hooks/useSfTeamActivitySummary';
import { activityCountsByReport } from '../../../support/salesforceTeamActivity';

import TeamMembersTable from './TeamMembersTable';
import SalesforceActivityDashboard from './SalesforceActivityDashboard';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';

interface Props {
  orgRows: OrgChartRow[];
  managerEmail: string;
  onSelectPerson: (email: string) => void;
  /** `all` or `range:YYYY-MM-DD:YYYY-MM-DD` from the Salesforce time dropdown. */
  timeFilter?: string;
  /** The time period in words (e.g. "This Fiscal Quarter"), for chart subtitles. */
  timeShort?: string;
  /** The fiscal calendar is still loading; hold data requests until the window is known. */
  timePending?: boolean;
}

interface SummaryNode {
  email?: string;
  is_manager?: boolean;
  own_total_opportunities?: number;
  total_opportunities?: number;
  reports?: Record<string, SummaryNode>;
}

interface TeamSummaryResponse {
  reports: Record<string, SummaryNode>;
}

/**
 * Leader > Salesforce > Team. The dashboard (a team total plus one chart per activity type) and the
 * "By Team Member" table read the same activity-summary tree, so a manager is always shown as their team's
 * rollup — never as just their own activities — and every number on the page agrees.
 */
export default function TeamSummaryPage({
  orgRows,
  managerEmail,
  onSelectPerson,
  timeFilter = 'all',
  timeShort = '',
  timePending = false,
}: Props) {
  const [isDashboardOpen, setIsDashboardOpen] = useState(true);

  const normalizedManagerEmail = String(managerEmail || '').toLowerCase();

  const {
    data,
    isLoading: isLoadingSummary,
    error: summaryError,
  } = useSfTeamOpportunities(
    normalizedManagerEmail,
    { enabled: !!normalizedManagerEmail && !timePending, view: 'summary' },
    timeFilter,
  ) as { data: TeamSummaryResponse | undefined; isLoading: boolean; error: unknown };

  const reportSummariesByEmail = useMemo(() => data?.reports || {}, [data]);

  const {
    data: activitySummary,
    isLoading: isLoadingActivity,
    error: activityError,
  } = useSfTeamActivitySummary(normalizedManagerEmail, timeFilter, {
    enabled: !!normalizedManagerEmail && !timePending,
  });
  const activityTree = activitySummary?.tree ?? null;
  const activityByEmail = useMemo(() => activityCountsByReport(activityTree), [activityTree]);

  if (!isManagerEmail(orgRows, normalizedManagerEmail)) {
    return <div className="hbr-css__text-secondary-sm">Selected user is not a manager.</div>;
  }

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <StbAccordion
        variant="minimal"
        label="Team Summary Dashboard"
        isOpen={isDashboardOpen}
        toggleIsOpen={setIsDashboardOpen}
      >
        <div style={{ padding: 12 }}>
          <SalesforceActivityDashboard
            tree={activityTree}
            loading={timePending || isLoadingActivity}
            error={activityError ? (activityError as Error).message || 'Failed to load activity.' : null}
            periodLabel={timeShort || 'Selected period'}
          />
        </div>
      </StbAccordion>

      <div style={{ height: 'calc(100% - 24px)', minHeight: 0 }}>
        {isLoadingSummary || timePending ? (
          <HbrSpinner>Loading team summary…</HbrSpinner>
        ) : summaryError ? (
          <div style={{ padding: 12, color: 'red' }}>
            {(summaryError as Error).message || 'Failed to load team summary.'}
          </div>
        ) : (
          <TeamMembersTable
            orgRows={orgRows}
            managerEmail={normalizedManagerEmail}
            reportSummariesByEmail={reportSummariesByEmail}
            activityByEmail={activityByEmail}
            activityLoading={isLoadingActivity}
            onSelectEmail={onSelectPerson}
          />
        )}
      </div>
    </div>
  );
}
